import PropTypes from "prop-types";
import BoxControls from "../../../Common/Box/BoxControls";
import Figure from "../../../Common/Box/Figure";
import ProductChooser from "../../ProductChooser";
import { getTooltip } from "../../../../utilities";
import theme from "../../../../theme";

const number = 5;

const getSeries = (data, theme) => {
  return [
    {
      name: "1: Maintain safety profile and method mix",
      data: data.map((country, index) => ({
        x: index,
        y: country.scenarios[0].value,
      })),
      color: theme.palette.scenarios.scenario1,
      marker: {
        fillColor: theme.palette.scenarios.scenario1,
      },
    },
    {
      name: "2: Shift safety profile",
      data: data.map((country, index) => ({
        x: index,
        y: country.scenarios[1].value,
      })),
      color: theme.palette.scenarios.scenario2,
      marker: {
        fillColor: theme.palette.scenarios.scenario2,
      },
    },
    {
      name: "3: Shift safety profile and method mix",
      data: data.map((country, index) => ({
        x: index,
        y: country.scenarios[2].value,
      })),
      color: theme.palette.scenarios.scenario3,
      marker: {
        fillColor: theme.palette.scenarios.scenario3,
      },
    },
  ];
};

const drawRange = (chart, countryCount, countryIndex, y1, y2) => {
  const countryOffset = countryCount - countryIndex;

  const xAxis = chart.xAxis[0];
  const yAxis = chart.yAxis[0];

  const itemGap = xAxis.translate(countryCount - 1.5);
  const itemCenter = xAxis.top + xAxis.translate(countryOffset) + itemGap;

  chart.renderer
    .path([
      "M",
      yAxis.left + yAxis.translate(y1),
      itemCenter,
      "L",
      yAxis.left + yAxis.translate(y2),
      itemCenter,
    ])
    .attr({
      "z-index": -1,
      "stroke-width": 1,
      stroke: theme.palette.grays.gray4,
    })
    .add();
};

const drawRanges = (chart, data) => {
  const countryCount = data.length;

  for (let i = 0; i < countryCount; i++) {
    const y1 = Math.min(...data[i].scenarios.map((scenario) => scenario.value));
    const y2 = Math.max(...data[i].scenarios.map((scenario) => scenario.value));

    drawRange(chart, countryCount, i, y1, y2);
  }
};

const options = (data) => (theme) => {
  const series = getSeries(data, theme);

  return {
    chart: {
      type: "scatter",
      width: 1000,
      height: 600,
      inverted: true,
      backgroundColor: null,
      events: {
        redraw: function () {
          drawRanges(this, data, series);
        },
      },
    },
    xAxis: {
      categories: data.map((country) => country.name),
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      opposite: true,
      labels: {
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      series: {
        marker: {
          symbol: "circle",
          radius: 4,
          lineWidth: 1,
          lineColor: "silver",
        },
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      reversed: false,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Services", this.y);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const ApacCcF5 = (props) => (
  <Figure
    id="APAC_CC_f5"
    number={number.toString()}
    title="Future Costed Abortion and PAC Services"
    subtitle={`${props.regionalUnitName} by Future Scenario, 2030`}
    chartOptions={props.data && options(props.data)}
    controls={
      <BoxControls>
        <ProductChooser
          area="abortion"
          value={props.productId}
          onChange={props.onProductChange}
        />
      </BoxControls>
    }
    annotations={true}
  />
);

ApacCcF5.propTypes = {
  productId: PropTypes.number.isRequired,
  regionalUnitName: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      scenarios: PropTypes.arrayOf(
        PropTypes.shape({
          scenario: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ),
  onProductChange: PropTypes.func,
};

export default ApacCcF5;
